/**
 * Geteilte Prüf- und Normalisierungslogik für Lead-Anfragen.
 * Wird im Browser (sofortige Inline-Fehler) und auf dem Server (verbindliche
 * Prüfung) verwendet, damit beide Seiten dieselben Regeln anwenden.
 */

/** Eindeutiger Marker für Anfragen ohne bestehenden Shop (keine erfundene URL). */
export const NO_SHOP_MARKER = "Noch kein Shop, Neuerstellung";

/** Rückwärtskompatibel: die zuvor live ausgelieferte Schreibweise bleibt gültig. */
export const LEGACY_NO_SHOP_MARKER = "Noch kein Shop – Neuerstellung";

export const NO_SHOP_MARKERS = [NO_SHOP_MARKER, LEGACY_NO_SHOP_MARKER];

export function isNoShopMarker(value: string): boolean {
  return NO_SHOP_MARKERS.includes(value.trim());
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;

export function isValidEmail(value: string): boolean {
  const v = value.trim();
  return v.length > 0 && v.length <= 255 && EMAIL_RE.test(v);
}

/**
 * Normalisiert eine Shop-Adresse mit oder ohne Protokoll.
 * Gibt null zurück, wenn die Eingabe keine brauchbare http(s)-Adresse ist.
 */
export function normalizeShopUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/\s/.test(trimmed)) return null;
  if (trimmed.length > 255) return null;

  const withScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;

  const host = url.hostname.toLowerCase();
  // Mindestens eine Punkt-getrennte Domain mit plausibler Endung.
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(host)) return null;

  const path = url.pathname === "/" ? "" : url.pathname.replace(/\/+$/, "");
  return `${url.protocol}//${host}${path}`;
}

export type LeadFieldError = "email" | "shop" | "message";

export type LeadInput = {
  email: string;
  shop: string;
  message: string;
  noShop?: boolean;
};

export type LeadCheck =
  | { ok: true; email: string; shop: string; message: string }
  | { ok: false; field: LeadFieldError; code: LeadErrorCode };

export type LeadErrorCode =
  | "email_required"
  | "email_invalid"
  | "shop_invalid"
  | "message_required"
  | "need_shop_or_message";

/**
 * Für eine qualifizierte Anfrage genügt neben der E-Mail entweder eine gültige
 * Shop-Adresse ODER eine kurze Beschreibung. Ohne bestehenden Shop wird die
 * Beschreibung verlangt, damit niemand eine Adresse erfinden muss.
 */
export function checkLead(input: LeadInput): LeadCheck {
  const email = input.email.trim();
  const rawShop = input.shop.trim();
  const message = input.message.trim();

  if (!email) return { ok: false, field: "email", code: "email_required" };
  if (!isValidEmail(email)) return { ok: false, field: "email", code: "email_invalid" };

  if (input.noShop) {
    if (message.length < 5) return { ok: false, field: "message", code: "message_required" };
    return { ok: true, email, shop: NO_SHOP_MARKER, message };
  }

  if (isNoShopMarker(rawShop)) {
    if (message.length < 5) return { ok: false, field: "message", code: "message_required" };
    return { ok: true, email, shop: NO_SHOP_MARKER, message };
  }

  let shop = "";
  if (rawShop) {
    const normalized = normalizeShopUrl(rawShop);
    if (!normalized) return { ok: false, field: "shop", code: "shop_invalid" };
    shop = normalized;
  }

  if (!shop && message.length < 5) {
    return { ok: false, field: "message", code: "need_shop_or_message" };
  }

  return { ok: true, email, shop, message };
}
